import { useCallback, useMemo } from 'react';
import { defaultRangeExtractor, type Range } from '@tanstack/react-virtual';

import type { BaseRowData } from '@adv-frontend/data-provider';

import type { GridRow } from '../../../types';

import type { VirtualBodyProps } from './VirtualBody.types';

interface AdditionalRowOptions {
    getRetainedIndexes?: () => readonly number[];
}

export function useRowVirtualizationOptions<RowData extends BaseRowData>(
    { rows, virtualization }: VirtualBodyProps<RowData>,
    { getRetainedIndexes }: AdditionalRowOptions = {}
) {
    const failLoadedIndexes = useMemo(() => {
        const indexes = new Set<number>();

        rows.forEach((row: GridRow<RowData>, index: number) => {
            if (row.original.status === 'load-failed') {
                indexes.add(index);
            }
        });

        return indexes;
    }, [rows]);

    const rangeExtractor = useCallback(
        (range: Range) => {
            const indexes = defaultRangeExtractor(range);
            const retainedIndexes = getRetainedIndexes?.() ?? [];

            if (retainedIndexes.length > 0) {
                indexes.push(...retainedIndexes.filter(index => !indexes.includes(index)));
            }

            indexes.sort((left, right) => left - right);
            return indexes.filter(index => !failLoadedIndexes.has(index));
        },
        [failLoadedIndexes, getRetainedIndexes]
    );

    const estimateRowSize = useCallback(
        (index: number) => {
            if (failLoadedIndexes.has(index)) {
                return 0;
            }

            return 'strictSize' in virtualization
                ? virtualization.strictSize(index) + 1
                : virtualization.estimateSize(index);
        },
        [virtualization, failLoadedIndexes]
    );

    return {
        ...virtualization,
        estimateSize: estimateRowSize,
        count: rows.length,
        rangeExtractor,
    };
}
